import React, { useEffect, useMemo, useState } from "react";
import { Search, Play, Calendar, ExternalLink, Youtube } from "lucide-react";
import api from "@/lib/api";

const formatDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

// Optional quick-filter chips (set search query when clicked)
const QUICK_FILTERS = ["Faith", "Family", "Prayer", "Prophecy", "Daniel", "Revival", "Youth", "Hope"];

export default function Sermons() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(true);
  const [channelUrl, setChannelUrl] = useState("");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/youtube/videos", { params: { limit: 50 } })
      .then((r) => {
        setConnected(r.data.connected);
        setChannelUrl(r.data.channel_url || "");
        const list = r.data.videos || [];
        setVideos(list);
        if (list.length) setActive(list[0]);
      })
      .catch(() => setConnected(false))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!query) return videos;
    const q = query.toLowerCase();
    return videos.filter((v) =>
      v.title.toLowerCase().includes(q) ||
      (v.description || "").toLowerCase().includes(q)
    );
  }, [videos, query]);

  return (
    <div data-testid="sermons-page">
      <section className="bg-[#003B5C] py-28 lg:py-36 relative grain-overlay">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-6">Sermons</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-white max-w-4xl">
            Messages that<br /><span className="text-[#D4A437]">inspire faith.</span>
          </h1>
          {channelUrl && (
            <a
              href={channelUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sermons-channel-link"
              className="inline-flex items-center gap-2 mt-8 text-sm text-white/80 hover:text-white border-b border-white/30 pb-0.5 transition-colors"
            >
              <Youtube size={16} className="text-[#D4A437]" />
              Subscribe on YouTube
              <ExternalLink size={12} />
            </a>
          )}
        </div>
      </section>

      {/* Featured player */}
      {active && (
        <section className="py-16 lg:py-20 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-8 aspect-video bg-[#003B5C] rounded-sm overflow-hidden">
                <iframe
                  key={active.id}
                  title={active.title}
                  src={active.embed_url}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="sermon-player-iframe"
                />
              </div>
              <div className="lg:col-span-4 flex flex-col justify-center">
                <div className="text-[#D4A437] text-xs font-bold uppercase tracking-[0.2em] mb-3">Now Playing</div>
                <h2 className="font-display text-2xl lg:text-3xl font-bold text-[#003B5C] leading-tight mb-4">
                  {active.title}
                </h2>
                {active.published && (
                  <div className="flex items-center gap-2 text-sm text-[#1E1E1E]/65 mb-4">
                    <Calendar size={14} className="text-[#D4A437]" /> {formatDate(active.published)}
                  </div>
                )}
                {active.description && (
                  <p className="text-sm leading-relaxed text-[#1E1E1E]/70 line-clamp-6">{active.description}</p>
                )}
                <a
                  href={active.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sermon-yt-link"
                  className="inline-flex items-center gap-2 mt-5 text-sm text-[#003B5C] font-semibold border-b-2 border-[#D4A437] pb-0.5 self-start"
                >
                  Watch on YouTube <ExternalLink size={12} />
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Archive */}
      <section className="py-16 lg:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          {!connected ? (
            <div data-testid="sermons-fallback" className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7 order-2 lg:order-1">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-5">Sermon Archive</div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#003B5C] leading-tight mb-6">
                  Watch every message on our YouTube channel.
                </h2>
                <p className="text-base sm:text-lg leading-relaxed text-[#1E1E1E]/75 mb-8 max-w-2xl">
                  All our sermons, worship messages, and livestream recordings are published on our
                  official YouTube channel — new videos posted every Sabbath.
                </p>
                <a
                  href={channelUrl || "https://www.youtube.com/@garlandebenezerghanaiansda"}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sermons-visit-youtube"
                  className="inline-flex items-center gap-2 bg-[#003B5C] text-white px-8 py-4 rounded-sm text-xs font-bold tracking-[0.18em] uppercase hover:bg-[#002A42] transition-all group"
                >
                  <Youtube size={16} className="text-[#D4A437]" />
                  Visit Our YouTube Channel
                  <ExternalLink size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
              </div>
              <div className="lg:col-span-5 order-1 lg:order-2">
                <div className="aspect-video bg-[#003B5C] rounded-2xl overflow-hidden relative grain-overlay">
                  <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#D4A437]/15" />
                  <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-[#D4A437]/10" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-[#D4A437] flex items-center justify-center">
                      <Play size={32} className="text-white ml-1" fill="currentColor" />
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3 bg-white/90 backdrop-blur-md rounded-sm px-4 py-3 z-10">
                    <Youtube size={20} className="text-[#003B5C]" />
                    <span className="text-sm font-semibold text-[#003B5C]">@garlandebenezerghanaiansda</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-8">
                <div className="relative flex-1">
                  <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1E1E1E]/40" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search sermons by title or speaker…"
                    data-testid="sermon-search-input"
                    className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-sm focus:outline-none focus:border-[#003B5C] text-[#1E1E1E] placeholder:text-[#1E1E1E]/40"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-10">
                <button
                  onClick={() => setQuery("")}
                  data-testid="sermon-quick-filter-all"
                  className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                    query === ""
                      ? "bg-[#003B5C] text-white"
                      : "bg-white text-[#1E1E1E]/70 border border-gray-200 hover:border-[#003B5C]"
                  }`}
                >
                  All
                </button>
                {QUICK_FILTERS.map((t) => (
                  <button
                    key={t}
                    onClick={() => setQuery(t)}
                    data-testid={`sermon-quick-filter-${t.toLowerCase()}`}
                    className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                      query.toLowerCase() === t.toLowerCase()
                        ? "bg-[#003B5C] text-white"
                        : "bg-white text-[#1E1E1E]/70 border border-gray-200 hover:border-[#003B5C]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {loading ? (
                <div data-testid="sermons-loading" className="text-center py-20 text-[#1E1E1E]/60">Loading sermons…</div>
              ) : filtered.length === 0 ? (
                <div data-testid="sermons-empty" className="text-center py-20 text-[#1E1E1E]/60">
                  No sermons match your search. Try a different keyword.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filtered.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => { setActive(v); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      data-testid={`sermon-card-${v.id}`}
                      className="group text-left bg-white border border-gray-100 rounded-sm overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all flex flex-col"
                    >
                      <div className="aspect-video relative bg-[#003B5C] overflow-hidden">
                        <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <div className="w-14 h-14 rounded-full bg-[#D4A437] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Play size={22} className="text-white ml-0.5" fill="currentColor" />
                          </div>
                        </div>
                      </div>
                      <div className="p-6 flex-1 flex flex-col">
                        <h3 className="font-display text-base sm:text-lg font-bold text-[#003B5C] leading-snug mb-3 line-clamp-3">{v.title}</h3>
                        {v.published && (
                          <div className="mt-auto flex items-center gap-2 text-xs text-[#1E1E1E]/55">
                            <Calendar size={12} className="text-[#D4A437]" /> {formatDate(v.published)}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
